import { Fragment, useState } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { KeenIcon } from '@/components';
import { useLayout } from '@/providers';
import { DMOInvitationsList } from './DMOInvitationsList';
import { DMOUsersList } from './DMOUsersList';
import { Modal } from '@/components/modal';
import { useSnackbar } from 'notistack';
import { useCreateDMOInvitationMutation } from '@/services/DMOManagementService';
import { ErrorApiResponse } from '@/types/api.types';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';

const DMOManagement = () => {
  const { currentLayout } = useLayout();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeView, setActiveView] = useState<'registered' | 'invitations'>(
    'registered'
  );
  const [createInvitation, { isLoading: isInviting }] =
    useCreateDMOInvitationMutation();

  const { hasPermission } = usePermissions();

  const canReadRegistrations = hasPermission(
    'dmo_registration_management_read'
  );
  const canReadInvitations = hasPermission('dmo_invitation_management_read');
  const canCreateRegistration = hasPermission(
    'dmo_registration_management_create'
  );
  const canCreateInvitation = hasPermission('dmo_invitation_management_create');
  const canReadStaffs = hasPermission('dmo_staff_management_read');

  const hasAnyDMOReadPermission = canReadRegistrations || canReadInvitations;

  const handleOpenInviteModal = () => {
    setIsInviteModalOpen(true);
    setEmail('');
    setEmailError(null);
    setExpiresAt('');
  };

  const handleOpenRegisterPage = () => {
    navigate('/dmo-management/register');
  };

  const handleOpenStaffManagement = () => {
    navigate('/dmo-management/staffs');
  };

  const handleCloseInviteModal = () => {
    setIsInviteModalOpen(false);
    setEmail('');
    setEmailError(null);
    setExpiresAt('');
  };

  const validateEmail = (value: string): string | null => {
    if (!value.trim()) return 'Email is required';
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!pattern.test(value.trim())) return 'Please enter a valid email';
    return null;
  };

  const handleSubmitInvite = async () => {
    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
      return;
    }

    try {
      const payload: { email: string; expires_at?: string } = {
        email: email.trim(),
      };

      if (expiresAt.trim()) {
        payload.expires_at = new Date(expiresAt).toISOString();
      }

      await createInvitation(payload).unwrap();
      enqueueSnackbar('Invitation sent successfully', { variant: 'success' });
      handleCloseInviteModal();
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      const message =
        (err as ErrorApiResponse)?.data?.message ||
        (err as ErrorApiResponse)?.message ||
        'Failed to send invitation';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  if (!hasAnyDMOReadPermission) {
    return (
      <Fragment>
        {currentLayout?.name === 'demo1-layout' && (
          <Container>
            <Toolbar>
              <ToolbarHeading>
                <ToolbarPageTitle />
                <ToolbarDescription>
                  Manage DMO users and invitations here.
                </ToolbarDescription>
              </ToolbarHeading>
            </Toolbar>
            <div className="bg-gray-100 p-6 rounded-lg shadow-lg flex flex-col">
              <div className="text-center py-4 text-gray-500" role="alert">
                You don't have permission to view DMO management.
              </div>
            </div>
          </Container>
        )}
      </Fragment>
    );
  }

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle id="dmo-management-heading" />
              <ToolbarDescription>
                {activeView === 'registered'
                  ? 'Manage registered DMO users.'
                  : 'Manage DMO invitations and track their status.'}
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              {activeView === 'registered' ? (
                <>
                  {canReadInvitations && (
                    <button
                      type="button"
                      className="btn btn-sm btn-light flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onClick={() => setActiveView('invitations')}
                      aria-label="Show DMO invitations"
                    >
                      <KeenIcon
                        type="filled"
                        icon="notepad"
                        className="text-gray-500"
                      />
                      DMO Invitations
                    </button>
                  )}
                  {canCreateRegistration && (
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={handleOpenRegisterPage}
                      aria-label="Register New DMO User"
                    >
                      Register New DMO User
                    </button>
                  )}

                  {canReadStaffs && (
                    <button
                      type="button"
                      className="btn btn-sm btn-light flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onClick={handleOpenStaffManagement}
                      aria-label="Manage DMO Staffs"
                    >
                      <KeenIcon
                        type="filled"
                        icon="users"
                        className="text-gray-500"
                      />
                      Manage DMO Staffs
                    </button>
                  )}
                </>
              ) : (
                <>
                  {canReadRegistrations && (
                    <button
                      type="button"
                      className="btn btn-sm btn-light flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onClick={() => setActiveView('registered')}
                      aria-label="Back to registered DMO users"
                    >
                      <KeenIcon
                        type="filled"
                        icon="arrow-left"
                        className="text-gray-500"
                      />
                      Back to registered DMO users
                    </button>
                  )}
                  {canCreateInvitation && (
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={handleOpenInviteModal}
                      aria-label="Invite new DMO admin"
                    >
                      Invite DMO Admin
                    </button>
                  )}
                </>
              )}
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}
      <Container>
        {activeView === 'registered' ? (
          <DMOUsersList />
        ) : (
          <DMOInvitationsList refreshKey={refreshKey} />
        )}
      </Container>

      <Modal
        open={isInviteModalOpen}
        onClose={handleCloseInviteModal}
        className="!flex items-center justify-center"
      >
        <div className="modal-content w-full max-w-md mx-4">
          <div className="modal-header">
            <h1 className="modal-title">Invite DMO Admin</h1>
            <button
              type="button"
              onClick={handleCloseInviteModal}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100 transition-colors"
              aria-label="Close"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="modal-body space-y-2">
            <label
              htmlFor="dmo-invite-email"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              Email
            </label>
            <input
              id="dmo-invite-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError(null);
              }}
              className="input input-md w-full"
              placeholder="dmo@example.com"
              autoComplete="off"
            />
            {emailError && (
              <p className="text-xs text-red-600 mt-1">{emailError}</p>
            )}

            <div className="mt-4 space-y-1">
              <label
                htmlFor="dmo-invite-expires-at"
                className="block text-sm font-medium text-gray-700 dark:text-gray-200"
              >
                Expiration date & time (optional)
              </label>
              <input
                id="dmo-invite-expires-at"
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="input input-md w-full"
                autoComplete="off"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                If left empty, the invitation link will expire automatically
                after 24 hours.
              </p>
            </div>
          </div>

          <div className="modal-footer flex justify-end gap-2">
            <button
              type="button"
              className="btn btn-light btn-sm"
              onClick={handleCloseInviteModal}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleSubmitInvite}
              disabled={isInviting}
            >
              {isInviting ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </div>
      </Modal>
    </Fragment>
  );
};

export { DMOManagement };
